import React, { useMemo, useState } from 'react';
import AtlasSidebar, { AtlasView } from './layout/AtlasSidebar';
import AtlasTopBar from './layout/AtlasTopBar';
import Dashboard from './Dashboard';
import ListaColaboradores from './Colaboradores';
import FichaColaborador from './Colaboradores/FichaColaborador';
import Organograma from './Organograma';
import Configuracoes from './Configuracoes';
import Permissoes from './Permissoes';
import DiscApp from './DISC';
import AuditoriaPage from './Auditoria';
import FeriasPage from './Ferias';
import TreinamentosApp from './Treinamentos';
import OnboardingApp from './Onboarding';
import FeedbackApp from './Feedback';
import DesempenhoApp from './Desempenho';
import PdiApp from './PDI';
import VagasApp from './Vagas';
import CargosSalarios from './CargosSalarios';
import PesquisasApp from './Pesquisas';
import KudosApp from './Kudos/Feed';
import LembretesApp from './Lembretes';
import DocumentosApp from './Documentos';
import SucessaoApp from './Sucessao/NoveBox';
import OffboardingApp from './Offboarding';
import DisciplinarApp from './Disciplinar';
import { AtlasUser } from './shared/atlasApi';

interface UserSession {
  usuario: {
    id?: string; nome?: string; email?: string; role?: string; is_admin?: boolean;
    permissoes?: { menus?: { modulo?: string; nivel?: string }[] };
  } | null;
  empresa: { id?: string; nome?: string } | null;
}

interface AtlasAppProps {
  session: UserSession;
  onVoltar: () => void;
}

const AtlasApp: React.FC<AtlasAppProps> = ({ session, onVoltar }) => {
  const usuario = session.usuario;
  const user: AtlasUser = useMemo(() => ({
    email: usuario?.email,
    empresaId: session.empresa?.id,
  }), [usuario?.email, session.empresa?.id]);

  const isAdmin = !!(
    usuario?.is_admin ||
    ['admin', 'administrator', 'company_admin', 'global_admin'].includes(
      (usuario?.role || '').toLowerCase()
    )
  );
  const nivelAtlas = (usuario?.permissoes?.menus || []).find(m => m.modulo === 'atlas')?.nivel || null;
  const podeEditar = isAdmin || nivelAtlas === 'editar' || nivelAtlas === 'rh_total';
  const firstName = (usuario?.nome || 'Usuário').split(' ')[0];

  const [view, setView] = useState<AtlasView>('dashboard');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fichaAberta, setFichaAberta] = useState<number | null>(null);
  const [busca, setBusca] = useState('');

  const trocarView = (v: AtlasView) => {
    setView(v);
    setFichaAberta(null);
    setBusca('');
    setMobileOpen(false);
  };

  const tituloPorView: Record<AtlasView, { titulo: string; sub?: string }> = {
    dashboard: { titulo: 'Dashboard', sub: 'Visão geral de pessoas' },
    colaboradores: { titulo: 'Colaboradores', sub: 'Funcionários ativos e demitidos' },
    organograma: { titulo: 'Organograma', sub: 'Distribuição por centro de custo' },
    disc: { titulo: 'Perfil DISC', sub: 'Mapeamento comportamental' },
    ferias: { titulo: 'Férias', sub: 'Saldo, programação e histórico' },
    treinamentos: { titulo: 'Treinamentos', sub: 'Capacitação e desenvolvimento' },
    onboarding: { titulo: 'Onboarding', sub: 'Trilhas de integração' },
    feedback: { titulo: 'Feedback 360', sub: 'Avaliação por pares' },
    desempenho: { titulo: 'Avaliação de Desempenho', sub: 'Ciclos, competências e resultados' },
    pdi: { titulo: 'PDI', sub: 'Plano de Desenvolvimento Individual' },
    disciplinar: { titulo: 'Ocorrências', sub: 'Advertências, suspensões e registros de conduta' },
    vagas: { titulo: 'Vagas', sub: 'Recrutamento e indicações' },
    cargos: { titulo: 'Cargos & Salários', sub: 'Faixas salariais por cargo e nível' },
    pesquisas: { titulo: 'Clima & eNPS', sub: 'Pesquisas de engajamento e clima' },
    kudos: { titulo: 'Reconhecimento', sub: 'Kudos e valores entre o time' },
    sucessao: { titulo: 'Sucessão & 9-box', sub: 'Matriz de desempenho x potencial' },
    documentos: { titulo: 'Documentos', sub: 'Vencimentos e documentos do colaborador' },
    lembretes: { titulo: 'Aniversariantes & Lembretes', sub: 'Datas, experiências e marcos' },
    offboarding: { titulo: 'Offboarding', sub: 'Desligamentos e entrevista de saída' },
    permissoes: { titulo: 'Permissões', sub: 'Controle de acesso ao Atlas' },
    auditoria: { titulo: 'Auditoria', sub: 'Trilha de operações sensíveis (LGPD)' },
    configuracoes: { titulo: 'Configurações', sub: 'Sincronização e integrações' },
  };
  const head = tituloPorView[view];

  const mostraBusca = view === 'colaboradores' && fichaAberta === null;

  let conteudo: React.ReactNode;
  if (view === 'dashboard') conteudo = <Dashboard user={user} />;
  else if (view === 'colaboradores') {
    conteudo = fichaAberta !== null
      ? <FichaColaborador user={user} funccodi={fichaAberta} podeEditar={podeEditar} onVoltar={() => setFichaAberta(null)} />
      : <ListaColaboradores user={user} busca={busca} podeEditar={podeEditar} onAbrir={setFichaAberta} />;
  }
  else if (view === 'organograma') conteudo = <Organograma user={user} podeEditar={podeEditar} onAbrir={f => { setView('colaboradores'); setFichaAberta(f); }} />;
  else if (view === 'disc') conteudo = <DiscApp user={user} podeEditar={podeEditar} onAbrirFicha={f => { setView('colaboradores'); setFichaAberta(f); }} />;
  else if (view === 'ferias') conteudo = <FeriasPage user={user} podeEditar={podeEditar} onAbrirFicha={f => { setView('colaboradores'); setFichaAberta(f); }} />;
  else if (view === 'treinamentos') conteudo = <TreinamentosApp user={user} podeEditar={podeEditar} />;
  else if (view === 'onboarding') conteudo = <OnboardingApp user={user} podeEditar={podeEditar} />;
  else if (view === 'feedback') conteudo = <FeedbackApp user={user} podeEditar={podeEditar} isAdmin={isAdmin} />;
  else if (view === 'desempenho') conteudo = <DesempenhoApp user={user} podeEditar={podeEditar} isAdmin={isAdmin} />;
  else if (view === 'pdi') conteudo = <PdiApp user={user} podeEditar={podeEditar} />;
  else if (view === 'disciplinar') conteudo = <DisciplinarApp user={user} podeEditar={podeEditar} onAbrirFicha={f => { setView('colaboradores'); setFichaAberta(f); }} />;
  else if (view === 'vagas') conteudo = <VagasApp user={user} podeEditar={podeEditar} />;
  else if (view === 'cargos') conteudo = <CargosSalarios user={user} podeEditar={podeEditar} />;
  else if (view === 'pesquisas') conteudo = <PesquisasApp user={user} podeEditar={podeEditar} />;
  else if (view === 'kudos') conteudo = <KudosApp user={user} podeEditar={podeEditar} />;
  else if (view === 'sucessao') conteudo = <SucessaoApp user={user} podeEditar={podeEditar} />;
  else if (view === 'documentos') conteudo = <DocumentosApp user={user} podeEditar={podeEditar} />;
  else if (view === 'lembretes') conteudo = <LembretesApp user={user} podeEditar={podeEditar} />;
  else if (view === 'offboarding') conteudo = <OffboardingApp user={user} podeEditar={podeEditar} />;
  else if (view === 'permissoes') conteudo = <Permissoes user={user} />;
  else if (view === 'auditoria') conteudo = <AuditoriaPage user={user} />;
  else if (view === 'configuracoes') conteudo = <Configuracoes user={user} isAdmin={isAdmin} />;
  else conteudo = (
    <div className="p-12 text-center text-gray-400">
      <h3 className="font-bold text-gray-700">Em construção</h3>
      <p className="text-sm mt-1">Esta seção será liberada em breve.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex">
      <AtlasSidebar
        current={view}
        onChange={trocarView}
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
        isAdmin={isAdmin}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <AtlasTopBar
          titulo={head.titulo}
          subtitulo={head.sub}
          onVoltar={onVoltar}
          userName={firstName}
          empresaNome={session.empresa?.nome}
          busca={mostraBusca ? busca : undefined}
          onBuscaChange={mostraBusca ? setBusca : undefined}
          buscaPlaceholder="Buscar colaborador por nome..."
          onMenuClick={() => setMobileOpen(true)}
        />
        <main className="flex-1 overflow-y-auto">
          {conteudo}
        </main>
      </div>
    </div>
  );
};

export default AtlasApp;
